import { reportErrorToBugsnag } from '@air/utils-error';

import { PENDING_SUBSCRIPTIONS, SUBSCRIPTIONS } from '~/constants/sockets';
import { sharedWebSocket, WS_CLOSE_CODE_LOGOUT } from '~/providers/SocketContext/create-socket/shared/constants';
import { type CreateOrJoinArgs } from '~/providers/SocketContext/create-socket/shared/typings';
import { type WebSocketMessage } from '~/providers/SocketContext/shared/typings';
import { ReadyState } from '~/types/sockets';
import { convertSubscriptionsToPending } from '~/utils/sockets/convert-subscriptions-to-pending';
import { getIterableSubscriptions } from '~/utils/sockets/get-iterable-subscriptions';

type AttachWsListenersArgs = {
  webSocketInstance: WebSocket;
} & Omit<CreateOrJoinArgs, 'url' | 'webSocketRef'>;

const handleSubscribeSuccess = (pendingUUID: string, subscriptionId: string) => {
  const pendingSubscription = PENDING_SUBSCRIPTIONS[pendingUUID];

  if (!pendingSubscription) {
    return;
  }

  // If the subscription already exists, just add the pending subscriptions listeners to its
  // set of listeners.
  if (SUBSCRIPTIONS[subscriptionId]) {
    pendingSubscription.subscribers.forEach(
      SUBSCRIPTIONS[subscriptionId].subscribers.add,
      SUBSCRIPTIONS[subscriptionId].subscribers,
    );
  } else {
    SUBSCRIPTIONS[subscriptionId] = { ...pendingSubscription, subscriptionId };
  }

  pendingSubscription.subscribers.forEach((subscriber) => {
    subscriber.options.current?.onSubscribe?.();
    subscriber.onSubscriptionSuccess(subscriptionId);
  });

  delete PENDING_SUBSCRIPTIONS[pendingUUID];
};

const bindMessageHandler = (webSocketInstance: WebSocket) => {
  webSocketInstance.onmessage = (message: WebSocketEventMap['message']) => {
    try {
      const data = JSON.parse(message.data);

      if (data.method === 'subscribe' && data.status === 'SUCCESS') {
        handleSubscribeSuccess(data.id, data.payload.subscriptionId);
      } else if (data.status === 'UPDATE') {
        const subscription = SUBSCRIPTIONS[data.payload.subscriptionId];

        if (subscription) {
          subscription.subscribers.forEach((subscriber) => {
            subscriber.options.current?.onUpdate?.(data.payload.event);
          });
        }
      }
    } catch (error) {
      reportErrorToBugsnag({ error, context: 'Failed to parse websocket message.', metadata: { message } });
    }
  };
};

const bindCloseHandler = (
  webSocketInstance: WebSocket,
  reconnect: () => void,
  setReadyState: (readyState: ReadyState) => void,
) => {
  if (webSocketInstance instanceof WebSocket) {
    webSocketInstance.onclose = (event) => {
      setReadyState(ReadyState.CLOSED);
      sharedWebSocket.ws = null;

      if (event.code === WS_CLOSE_CODE_LOGOUT) {
        return;
      }

      reconnect();
    };
  }
};

const bindErrorHandler = (webSocketInstance: WebSocket, setReadyState: (readyState: ReadyState) => void) => {
  webSocketInstance.onerror = (error: WebSocketEventMap['error']) => {
    const subscriptionsArray = getIterableSubscriptions();

    subscriptionsArray.forEach((subscription) => {
      subscription.subscribers.forEach((subscriber) => {
        subscriber.options.current?.onError?.(error);
      });
    });

    setReadyState(ReadyState.CLOSED);
  };
};

const bindOpenHandler = (
  webSocketInstance: WebSocket,
  setReadyState: (readyState: ReadyState) => void,
  messageQueue: AttachWsListenersArgs['messageQueue'],
) => {
  webSocketInstance.onopen = () => {
    // When the socket opens, convert any previously subscribed subscriptions to pending subscriptions
    // and re-send subscriptions messages for each. This conversion is required because the server treats each new client
    // connection as a new client, and will use different subscriptionIds, so we have to treat them as new client side as well.
    convertSubscriptionsToPending();

    const messages: WebSocketMessage[] = [];

    Object.keys(PENDING_SUBSCRIPTIONS).forEach((key) => {
      const subscription = PENDING_SUBSCRIPTIONS[key];

      if (subscription) {
        messages.push(
          JSON.stringify({
            method: 'subscribe',
            id: key,
            args: {
              eventType: subscription.event.eventType,
              pattern: { ...subscription.event.pattern, workspaceId: subscription.workspaceId },
            },
          }),
        );
      }
    });

    messageQueue.current = messageQueue.current.concat(messages);

    setReadyState(ReadyState.OPEN);
  };
};

export const attachWsListeners = ({
  webSocketInstance,
  setReadyState,
  reconnect,
  messageQueue,
}: AttachWsListenersArgs) => {
  bindMessageHandler(webSocketInstance);
  bindCloseHandler(webSocketInstance, reconnect, setReadyState);
  bindErrorHandler(webSocketInstance, setReadyState);
  bindOpenHandler(webSocketInstance, setReadyState, messageQueue);
};
